// utils/shipRequestGate.js
//
// What the «طلب الشحن» button on an ACTIVE carton should do, decided from the
// carton's own lines. Pure — reads plain `{ status }` objects, so it behaves
// identically on a pinia-orm OrderBoxItem and on a raw payload (tests).
//
// The backend owns the rule and enforces it (OrderBoxService::requestShipping,
// ship-ready pack S-A / S-B): the server refuses with a 409 and her own sentence
// whenever this gate would have. This is the PWA declining to OFFER a button the
// server would refuse — never a second source of truth. When the two disagree the
// server wins and the caller re-reads the carton.
//
// The lists come from ItemStatusCategories — nothing is spelled out here, so a
// status added to the backend's "on its way" set is one edit in ItemStatusConfig.
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { ItemStatusCategories } from '@/enums/commerce/ItemStatusConfig.js';

/** The states the ask-to-ship button can be in, in the order they are decided. */
export const ShipGate = Object.freeze({
  /** The carton's lines are still being read — decide nothing, claim nothing. */
  LOADING: 'loading',
  /** A line at ISSUE — she has a decision to make first (today's «حل المشاكل أولًا»). */
  ISSUES: 'issues',
  /** A line is still on its way — wait for it, or cancel the ones she no longer wants. */
  ON_ITS_WAY: 'onItsWay',
  /** Not one line has arrived — there is nothing to pack yet. */
  NOTHING_ARRIVED: 'nothingArrived',
  /** Ask to ship. */
  READY: 'ready',
});

/**
 * @param {Array<{status: number|string}>|null|undefined} items the carton's lines
 * @param {{loading?: boolean}} [options] `loading` — the lines are still being
 *   fetched. The gate then answers LOADING whatever the lines say, because a
 *   half-loaded carton reads as «nothing arrived» and would tell her something false.
 * @returns {{
 *   state: string,
 *   issueCount: number,
 *   onItsWayCount: number,
 *   arrivedCount: number,
 *   outOfStock: Array<object>,
 * }}
 *   `onItsWayCount` counts every line on the ON_ITS_WAY list, ISSUE included —
 *   but ISSUE is decided first, so whenever the state is ON_ITS_WAY it is the
 *   count of the waiting lines and no ISSUE line is among them.
 *   `outOfStock` is the lines she has to approve the cancelling of: the very
 *   objects to show her, and the source of the ids to send.
 */
export function shipRequestGate(items, { loading = false } = {}) {
  const lines = Array.isArray(items) ? items : [];

  let issueCount = 0;
  let onItsWayCount = 0;
  let arrivedCount = 0;
  const outOfStock = [];

  for (const line of lines) {
    const status = Number(line?.status);

    if (ItemStatusCategories.BLOCKS_SHIPPING.includes(status)) issueCount += 1;
    if (ItemStatusCategories.ON_ITS_WAY.includes(status)) onItsWayCount += 1;
    if (ItemStatusCategories.ARRIVED.includes(status)) arrivedCount += 1;
    if (status === ItemStatus.OUT_OF_STOCK) outOfStock.push(line);
  }

  let state = ShipGate.READY;
  if (loading) state = ShipGate.LOADING;
  else if (issueCount > 0) state = ShipGate.ISSUES;
  else if (onItsWayCount > 0) state = ShipGate.ON_ITS_WAY;
  else if (arrivedCount === 0) state = ShipGate.NOTHING_ARRIVED;

  return { state, issueCount, onItsWayCount, arrivedCount, outOfStock };
}

/**
 * The body of the ask-to-ship request: the ids she approved cancelling, as the
 * strings the wire contract wants (ship-ready pack S-K).
 *
 * Takes the lines the confirm dialog SHOWED her — not a fresh read of the carton —
 * so the list she agreed to and the list we send are the same list by
 * construction. The server checks the other direction: every line at OUT_OF_STOCK
 * at that moment must be in it, or it refuses and cancels nothing.
 *
 * @param {Array<{id: string|number}>} shownLines
 * @returns {string[]}
 */
export function approvedOutOfStockIds(shownLines) {
  return (Array.isArray(shownLines) ? shownLines : [])
    .map((line) => line?.id)
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map(String);
}
